import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
} from '@ag-grid-community/core'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Tag, Tooltip, message } from '@surecompanies/core_components'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import {
  BulkImportStatus,
  type BulkImportSessionResponseDto,
  type OverrideCategory,
} from '../api/dto/bulk-import-session.dto'
import {
  downloadBulkImportFile,
  listBulkImportSessions,
} from '../api/plan-override-bulk-imports-api'
import styles from './PlanOverridesComponents.module.scss'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50]

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

const StatusCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data) return null
  const tagProps: Record<BulkImportStatus, { color: string; label: string }> = {
    [BulkImportStatus.COMPLETED]: { color: 'green', label: 'Completed' },
    [BulkImportStatus.PROCESSING]: { color: 'blue', label: 'Processing' },
    [BulkImportStatus.FAILED]: { color: 'red', label: 'Failed' },
  }
  const { color, label } = tagProps[data.status] ?? {
    color: 'default',
    label: data.status,
  }
  return <Tag color={color}>{label}</Tag>
}

const RecordsCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data) return null
  if (
    data.totalRows == null ||
    (data.totalRows === 0 && data.status === BulkImportStatus.PROCESSING)
  )
    return <span>—</span>
  if (data.status === BulkImportStatus.PROCESSING) {
    return (
      <span>
        {data.processedRows} of {data.totalRows}
      </span>
    )
  }
  return (
    <span>
      {data.successCount ?? 0} of {data.totalRows}
    </span>
  )
}

const UploadedByCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data) return null
  const name = data.uploadedByName ?? data.uploadedBy ?? '—'
  return <span>{name}</span>
}

type FileRole = 'original' | 'success' | 'errors'

interface FileCellProps {
  id: string
  role: FileRole
  filename: string
  tooltip: string
}

function FileCell({ id, role, filename, tooltip }: FileCellProps) {
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      if (loading) return
      setLoading(true)
      try {
        const { url } = await downloadBulkImportFile(id, role)
        window.open(url, '_blank')
      } catch {
        message.error(
          'Could not retrieve file. It may have been deleted or expired.'
        )
      } finally {
        setLoading(false)
      }
    },
    [id, role, loading]
  )

  const linkClassName = [styles.fileLink, loading ? styles.fileLinkLoading : '']
    .filter(Boolean)
    .join(' ')

  return (
    <Tooltip title={tooltip} placement="topLeft">
      <a
        href="#"
        onClick={handleClick}
        aria-label={tooltip}
        className={linkClassName}
      >
        <DownloadOutlined className={styles.fileLinkIcon} />
        <span className={styles.fileLinkName}>{filename}</span>
      </a>
    </Tooltip>
  )
}

const OriginalFileCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data || !data.hasOriginalFile) return <span>—</span>
  const base = data.originalFileName ?? 'file.csv'
  return (
    <FileCell
      id={data.id}
      role="original"
      filename={base}
      tooltip="Original file"
    />
  )
}

const SuccessFileCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data || !data.hasSuccessFile) return <span>—</span>
  return (
    <FileCell
      id={data.id}
      role="success"
      filename="success.csv"
      tooltip="success.csv"
    />
  )
}

const ErrorsFileCellRenderer = ({
  data,
}: ICellRendererParams<BulkImportSessionResponseDto>) => {
  if (!data || !data.hasErrorsFile) return <span>—</span>
  return (
    <FileCell
      id={data.id}
      role="errors"
      filename="errors.csv"
      tooltip="errors.csv"
    />
  )
}

interface Props {
  overrideType: OverrideCategory
  onTotalChange?: (total: number) => void
  // Parent bumps to force a full server-side refresh (e.g., after upload or polling completion).
  refreshSignal?: number
  // Parent bumps on each poll tick while PROCESSING — triggers a soft refresh that preserves the current page.
  pollingRefreshSignal?: number
  defaultPageSize?: number
  onPageSizeChange?: (size: number) => void
}

export default function BulkImportSessionsContent({
  overrideType,
  onTotalChange,
  refreshSignal,
  pollingRefreshSignal,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
}: Props) {
  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const gridInitializedRef = useRef(false)

  const handleFirstDataRendered = useCallback(() => {
    gridInitializedRef.current = true
  }, [])

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<BulkImportSessionResponseDto>) => {
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageSizeChange]
  )

  const [grid, setGrid] =
    useState<GridReadyEvent<BulkImportSessionResponseDto>>()

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<BulkImportSessionResponseDto>
      ) => {
        try {
          const { startRow, endRow } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1

          const result = await listBulkImportSessions({
            page,
            limit: pageSize,
            overrideType,
          })

          onTotalChangeRef.current?.(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load bulk import sessions', error)
          params.fail()
        }
      },
    }),
    [overrideType]
  )

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  useEffect(() => {
    if (!grid || !refreshSignal) return
    grid.api.refreshServerSide({ purge: true })
  }, [grid, refreshSignal])

  useEffect(() => {
    if (!grid || !pollingRefreshSignal) return
    grid.api.refreshServerSide({ purge: false })
  }, [grid, pollingRefreshSignal])

  const columnDefs = useMemo<ColDef<BulkImportSessionResponseDto>[]>(
    () => [
      {
        headerName: 'Uploaded',
        field: 'startedAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        width: 160,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: StatusCellRenderer,
        width: 150,
      },
      {
        headerName: 'Records',
        cellRenderer: RecordsCellRenderer,
        width: 130,
      },
      {
        headerName: 'Uploaded by',
        cellRenderer: UploadedByCellRenderer,
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: 'Original',
        cellRenderer: OriginalFileCellRenderer,
        flex: 2,
        minWidth: 160,
      },
      {
        headerName: 'Success',
        cellRenderer: SuccessFileCellRenderer,
        flex: 2,
        minWidth: 160,
      },
      {
        headerName: 'Errors',
        cellRenderer: ErrorsFileCellRenderer,
        flex: 2,
        minWidth: 160,
      },
    ],
    []
  )

  return (
    <div className={`ag-theme-alpine ${styles.historyGridAutoHeight}`}>
      <AgGridReact
        getRowId={({ data }) => data.id}
        rowModelType="serverSide"
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        suppressRowClickSelection
        overlayLoadingTemplate="Loading..."
        overlayNoRowsTemplate="No bulk imports yet"
        domLayout="autoHeight"
        rowHeight={52}
        pagination
        paginationPageSize={defaultPageSize}
        paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
        cacheBlockSize={defaultPageSize}
        onGridReady={setGrid}
        onFirstDataRendered={handleFirstDataRendered}
        onPaginationChanged={handlePaginationChanged}
      />
    </div>
  )
}
